import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { getPreferencesByTenantId, updateDashboardPreferencesByTenantIdAndFeature, getUserPreferencesByUserId, updateUserPreferencesByUserIdAndFeature } from "../api/tenantApi"; 
import { getTenantIdAuth0, isAdminAuth0, getCurrentUserId } from "../authUtils";
import getTenantIdFromURL from "../utils/getTenantIdFromURL";

const UserPreferencesContext = createContext(undefined);

export const UserPreferencesProvider = ({ children }) => {
  const [userPreferences, setUserPreferences] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUserPreferences = async () => {
      try {
        console.log('🚀 UserPreferences fetching preferences...');
        const isAdmin = await isAdminAuth0();
        const selectedTenant = getTenantIdFromURL();
        const tenantId = isAdmin ? selectedTenant : await getTenantIdAuth0();
        const userId = getCurrentUserId();
        
        console.log("tenantId here in preference", tenantId);
        console.log("userId here in preference", userId);
        
        const preferences = {};
        
        // Fetch tenant-specific preferences (for Slack)
        try {
          const tenantResult = await getPreferencesByTenantId(tenantId);
          console.log("INFO Tenant Preferences result -> ", JSON.stringify(tenantResult, null, 2));
          
          if (tenantResult && tenantResult.length > 0) {
            tenantResult.forEach((item) => {
              // Only include Slack preferences from tenant
              if (item.feature === "Slack Notification Preferences") {
                preferences.slack = JSON.parse(item.preferences);
              }
            });
          }
        } catch (error) {
          console.error("Error fetching tenant preferences:", error);
        }
        
        // Fetch user-specific preferences (for inApp, email, weekly)
        if (userId) {
          try {
            const userResult = await getUserPreferencesByUserId(userId);
            console.log("INFO User Preferences result -> ", JSON.stringify(userResult, null, 2));
            
            if (userResult && userResult.length > 0) {
              userResult.forEach((item) => {
                // Map user preferences to the correct channel
                switch (item.feature) {
                  case "Notification Preferences":
                    preferences.inApp = JSON.parse(item.preferences);
                    break;
                  case "Email Notification Preferences":
                    preferences.email = JSON.parse(item.preferences);
                    break;
                  case "Weekly Notification Preferences":
                    preferences.weekly = JSON.parse(item.preferences);
                    break;
                  default:
                    break;
                }
              });
            }
          } catch (error) {
            console.error("Error fetching user preferences:", error);
          }
        }
        
        setUserPreferences(preferences);
        console.log("INFO Combined User Preferences -> ", preferences);
      } catch (error) {
        console.error("Unable to fetch user preferences. Error JSON:", JSON.stringify(error, null, 2));
      } finally {
        setLoading(false);
      }
    };

    fetchUserPreferences();
  }, []);

  const updateUserPreferences = async (tenantId, feature, preferences) => {
    try {
      // Determine if this is a user-specific or tenant-specific preference
      const isUserSpecific = feature !== "Slack Notification Preferences";
      
      if (isUserSpecific) {
        const userId = getCurrentUserId();
        if (!userId) {
          throw new Error('User ID not found');
        }
        await updateUserPreferencesByUserIdAndFeature(userId, feature, preferences);
      } else {
        await updateDashboardPreferencesByTenantIdAndFeature(tenantId, feature, preferences);
      }
      
      setUserPreferences(prev => ({
        ...prev,
        [feature]: preferences
      }));
    } catch (error) {
      console.error("Unable to update user preferences:", error);
    }
  };

  return (
    <UserPreferencesContext.Provider 
      value={{ userPreferences, loading, updateUserPreferences }}
    >
      {children}
    </UserPreferencesContext.Provider>
  );
};

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};
